import { ApplicationRef, Component } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { concat, first, interval } from 'rxjs';

// registerWhenStable only checks for an update once, right after startup — a tab left
// open across a deploy would otherwise never notice a new version exists.
const UPDATE_CHECK_INTERVAL_MS = 10 * 60 * 1000;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  constructor(
    private appRef: ApplicationRef,
    private swUpdate: SwUpdate,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer
  ) {
    this.translate.addLangs(['fr-FR', 'en-US']);
    this.translate.setDefaultLang('fr-FR');

    //svg icons
    this.matIconRegistry.addSvgIcon(
      `shackle`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/indicator/shackle.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `shackle_full`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/indicator/shackle_full.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `battery_10`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/indicator/battery_10.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `battery_40`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/indicator/battery_40.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `battery_empty`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/indicator/battery_empty.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `battery_full`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/indicator/battery_full.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `battery_unknown`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/indicator/battery_unknown.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `horizontal_points`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/horizontal_points.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `refresh`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/refresh.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `resize`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/resize.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `drag_box`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/drag_box.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `detection_and_zone`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/detection_and_zone.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `detection_and_zone_off`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/detection_and_zone_off.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `z42`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`/img/icons/z42.svg`)
    );

    this.watchForAppUpdates();
  }

  private watchForAppUpdates() {
    if (!this.swUpdate.isEnabled) return;

    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    concat(appIsStable$, interval(UPDATE_CHECK_INTERVAL_MS)).subscribe(() => {
      this.swUpdate.checkForUpdate().catch(err => console.error('❌ Update check failed:', err));
    });

    this.swUpdate.versionUpdates.subscribe(async event => {
      if (event.type !== 'VERSION_READY') return;

      await this.swUpdate.activateUpdate();
      this.promptReload();
    });
  }

  private promptReload() {
    // Stays open until the user acts on it (no `duration`) — replaces the old
    // auto-dismissing toast that most users never got to click in time.
    const snackBarRef = this.snackBar.open(
      this.translate.instant('app_update.available'),
      this.translate.instant('app_update.reload'),
      { panelClass: ['primary-toast'] }
    );

    snackBarRef.onAction().subscribe(() => document.location.reload());
  }
}
